/**
 * Feueralarm: zuerst Vollbild, danach verkleinerbar.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import API from '../api/api';
import { Icon } from '../icons/AppIcons';
import { resetAlarmWithTrace } from '../utils/actionTrace';
import '../css/alarm.css';

const FireApi = new API();

const PORT_ICONS = {
  fire: 'fire',
  door: 'door',
  none: 'emptyPort',
};

const PORT_LABELS = {
  fire: 'Feuermelder',
  door: 'Türkontakt',
  none: 'Anschluss',
};

function fmt(value, suffix) {
  if (value === null || value === undefined || value === '') return '–';
  const n = Number(value);
  if (Number.isNaN(n)) return `${value}${suffix || ''}`;
  return `${Number.isInteger(n) ? n : n.toFixed(1)}${suffix || ''}`;
}

function portType(alarm, index) {
  return alarm[`port_${index}`] || 'none';
}

function portLive(alarm, index) {
  const keys = ['device_alarm_a', 'device_alarm_b', 'device_alarm_c', 'device_alarm_d'];
  const eventKeys = ['adapter_a', 'adapter_b', 'adapter_c', 'adapter_d'];
  if (alarm.device_alarm_checked) {
    return Boolean(alarm[keys[index - 1]]);
  }
  return Boolean(alarm[eventKeys[index - 1]]);
}

function portVoltage(alarm, index) {
  return alarm[`voltage_${index}`];
}

export default function AlarmOverlay() {
  const [alarms, setAlarms] = useState([]);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const seenIds = useRef(new Set());

  const load = useCallback(() => {
    return FireApi.endpoints.events.getAll(
      { withCredentials: true, crossDomain: true },
      { page: 1, page_size: 50, confirmed: '0' }
    ).then((res) => {
      let list = [];
      if (res?.data?.results && Array.isArray(res.data.results)) list = res.data.results;
      else if (Array.isArray(res?.data)) list = res.data;
      const byDevice = [];
      const seen = new Set();
      list.forEach((item) => {
        const key = item.device_id || item.id;
        if (seen.has(key)) return;
        seen.add(key);
        byDevice.push(item);
      });
      const prevIds = seenIds.current;
      const nextIds = new Set(byDevice.map((item) => String(item.device_id || item.id)));
      const newIndex = byDevice.findIndex((item) => !prevIds.has(String(item.device_id || item.id)));
      seenIds.current = nextIds;
      setAlarms(byDevice);
      if (newIndex >= 0) {
        setMinimized(false);
        setIndex(newIndex);
      }
      return byDevice;
    }).catch(() => null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let since = 0;

    load();

    const watch = async () => {
      while (!cancelled) {
        try {
          const res = await FireApi.endpoints.live.wait(since);
          if (cancelled) return;
          const data = res?.data;
          if (data && typeof data.revision === 'number') {
            since = data.revision;
            await load();
          } else {
            await load();
          }
        } catch (_) {
          if (cancelled) return;
          await new Promise((resolve) => setTimeout(resolve, 1500));
          await load();
        }
      }
    };

    watch();
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    if (index >= alarms.length && alarms.length > 0) {
      setIndex(0);
    }
  }, [alarms, index]);

  useEffect(() => {
    if (alarms.length === 0) {
      document.documentElement.removeAttribute('data-fire-alarm');
      return;
    }
    document.documentElement.setAttribute('data-fire-alarm', minimized ? 'bar' : 'full');
    return () => document.documentElement.removeAttribute('data-fire-alarm');
  }, [minimized, alarms.length]);

  const alarm = alarms[index] || null;

  const tryReset = async (current, event) => {
    if (!current || busy) return;
    setBusy(true);
    const data = await resetAlarmWithTrace(FireApi, current.id, event);
    if (data?.status === 'success') {
      const deviceId = current.device_id;
      setAlarms((prev) => prev.filter((item) => item.device_id !== deviceId && item.id !== current.id));
    }
    setBusy(false);
  };

  if (!alarm) {
    return null;
  }

  const ports = [1, 2, 3, 4].map((n) => {
    const type = portType(alarm, n);
    return {
      n,
      live: portLive(alarm, n),
      type,
      icon: PORT_ICONS[type] || 'emptyPort',
      label: `${PORT_LABELS[type] || 'Anschluss'} ${n}`,
      volt: fmt(portVoltage(alarm, n), ' V'),
    };
  });

  if (minimized) {
    return (
      <div className="fire-alarm-bar" role="status" aria-live="polite">
        <button type="button" className="fire-alarm-bar-expand" onClick={() => setMinimized(false)}>
          Details
        </button>
        <div className="fire-alarm-bar-label">Feueralarm</div>
        <div className="fire-alarm-bar-name">{alarm.device_name || 'Koffer'}</div>
        <div className="fire-alarm-bar-ports">
          {ports.map((p) => (
            <span key={p.n} className={p.live ? 'is-on' : 'is-off'} title={p.label}>
              <Icon name={p.icon} size={16} />
            </span>
          ))}
        </div>
        <div className="fire-alarm-bar-metrics">
          <Icon name="temperature" size={16} />
          {fmt(alarm.temperature, ' °C')}
          <span>·</span>
          <Icon name="humidity" size={16} />
          {fmt(alarm.humidity, ' %')}
          <span>·</span>
          <Icon name="battery" size={16} />
          {fmt(alarm.battery, ' V')}
        </div>
        <button
          type="button"
          className="fire-alarm-bar-reset"
          disabled={busy}
          onClick={(event) => tryReset(alarm, event)}
        >
          Zurückstellen
        </button>
      </div>
    );
  }

  return (
    <div className="fire-alarm-overlay" role="alertdialog" aria-live="assertive">
      <button
        type="button"
        className="fire-alarm-minimize"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMinimized(true);
        }}
      >
        Verkleinern
      </button>
      <div className="fire-alarm-center">
        <div className="fire-alarm-panel">
          <div className="fire-alarm-banner">FEUERALARM</div>
          <h1 className="fire-alarm-title">{alarm.device_name || 'Koffer'}</h1>
          <p className="fire-alarm-type">{alarm.type || 'ALARM'}</p>
          {alarms.length > 1 && (
            <div className="fire-alarm-switch">
              <button type="button" disabled={index === 0} onClick={() => setIndex((i) => Math.max(0, i - 1))}>Zurück</button>
              <span>{index + 1} / {alarms.length}</span>
              <button type="button" disabled={index >= alarms.length - 1} onClick={() => setIndex((i) => Math.min(alarms.length - 1, i + 1))}>Weiter</button>
            </div>
          )}

          <div className="fire-alarm-ports">
            {ports.map((p) => (
              <div key={p.n} className={`fire-alarm-port ${p.live ? 'is-on' : 'is-off'}`}>
                <div className="fire-alarm-port-index">
                  <Icon name={p.icon} size={28} />
                </div>
                <div className="fire-alarm-port-label">{p.label}</div>
                <div className="fire-alarm-port-state">{p.live ? 'ALARM' : 'Ruhe'}</div>
                <div className="fire-alarm-port-volt">{p.volt}</div>
              </div>
            ))}
          </div>

          <div className="fire-alarm-metrics">
            <div>
              <span>Temperatur</span>
              <strong><Icon name="temperature" size={18} /> {fmt(alarm.temperature, ' °C')}</strong>
            </div>
            <div>
              <span>Feuchte</span>
              <strong><Icon name="humidity" size={18} /> {fmt(alarm.humidity, ' %')}</strong>
            </div>
            <div>
              <span>Batterie</span>
              <strong><Icon name="battery" size={18} /> {fmt(alarm.battery, ' V')}</strong>
            </div>
            <div>
              <span>Signal</span>
              <strong><Icon name="signal" size={18} /> {fmt(alarm.signal, '')}</strong>
            </div>
          </div>

          <div className="fire-alarm-actions">
            <button
              type="button"
              className="fire-alarm-reset"
              disabled={busy}
              onClick={(event) => tryReset(alarm, event)}
            >
              Zurückstellen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
